import { createFormInviteLink, destroyAllFormInviteLinks, getFormInviteLinks } from '@/lib/api/formCooperatorApi'
import { CooperatorUserRole, GetFormInviteLinksQuery } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { QueryState } from './queryStateType'

export type CooperatorLink = NonNullable<NonNullable<GetFormInviteLinksQuery['form']>['cooperatorLinks']>[number] & {
  formToken: string
}

type StateType = {
  queryState: { [formToken: string]: QueryState }
}

const initialState: StateType = { queryState: {} }

const formInviteLinkAdapter = createEntityAdapter<CooperatorLink, string>({
  selectId: model => model.token,
})

const formInviteLinkSlice = createSlice({
  name: 'formInviteLinks',
  initialState: formInviteLinkAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(createFormInviteLinkThunk.fulfilled, (state, action) => {
        formInviteLinkAdapter.addOne(state, { ...action.payload, formToken: action.meta.arg.formToken })
      })
      .addCase(destroyAllFormInviteLinksThunk.fulfilled, (state, action) => {
        const ids = Object.values(state.entities)
          .filter(link => link.formToken === action.meta.arg.formToken)
          .map(link => link.token)
        formInviteLinkAdapter.removeMany(state, ids)
      })
      .addCase(fetchFormInviteLinksThunk.pending, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken]!.isLoading = true
        } else {
          state.queryState[formToken] = { isLoading: true, loaded: false, error: null }
        }
      })
      .addCase(fetchFormInviteLinksThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        formInviteLinkAdapter.upsertMany(
          state,
          action.payload.map(link => ({ ...link, formToken }))
        )
        if (state.queryState[formToken]) {
          state.queryState[formToken]!.isLoading = false
          state.queryState[formToken]!.loaded = true
          state.queryState[formToken]!.error = null
        }
      })
      .addCase(fetchFormInviteLinksThunk.rejected, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken]!.isLoading = false
          state.queryState[formToken]!.error = action.error.message
        }
      })
  },
})

export const fetchFormInviteLinksThunk = createAsyncThunk<
  NonNullable<NonNullable<GetFormInviteLinksQuery['form']>['cooperatorLinks']>,
  { formToken: string }
>('formInviteLinks/fetchAll', async (payload, thunkAPI) => {
  const { data } = await getFormInviteLinks(payload.formToken)
  return data?.form?.cooperatorLinks
    ? data.form.cooperatorLinks
    : thunkAPI.rejectWithValue('failed to load form invite links')
})

export const createFormInviteLinkThunk = createAsyncThunk<
  CooperatorLink,
  { formToken: string; formRoleId: string; userRole: CooperatorUserRole }
>('formInviteLinks/create', async (payload, thunkAPI) => {
  const { data } = await createFormInviteLink({
    formToken: payload.formToken,
    formRoleId: payload.formRoleId,
    userRole: payload.userRole,
  })
  return (
    (data?.createFormCooperatorLink?.cooperatorLink as CooperatorLink) ||
    thunkAPI.rejectWithValue('create form invite link failed')
  )
})

export const destroyAllFormInviteLinksThunk = createAsyncThunk<string, { formToken: string }>(
  'formInviteLinks/destroyAll',
  async payload => {
    const { data } = await destroyAllFormInviteLinks({ formToken: payload.formToken })
    return data?.destroyFormCooperatorLinks?.status || Promise.reject('destroy all form invite links failed')
  }
)

export const selectFormInviteLinksByFormToken = createSelector(
  (state: RootState) => state.formInviteLinks.entities,
  (_state: RootState, formToken: string) => formToken,
  (entities, formToken) => Object.values(entities).filter(link => link.formToken === formToken)
)

export default formInviteLinkSlice.reducer
